import React, { useContext } from 'react';
import i18next from 'i18next';
import { Textlink } from '../../../../AutostadtTabComponents/Button';
import SubpageBlue from '../../SubpageBlue';
import './styles.scss';
import Checklist from '../../../../AutostadtTabComponents/Checklist';
import Infobox from '../../../../AutostadtTabComponents/Tiles/Infobox';
import { Trans } from 'react-i18next';
import { usePersistedChecklist } from '../../../../../utils/usePersistedChecklist';
import SspProviderContext from '../../../../../utils/SspProvider/context';

export default function ChecklistPage() {
    const electricCar =
        useContext(SspProviderContext).state.content?.asModel.ref('engine').asModel.str('engine_type') === 'Electric';

    const generalChecklistItems = [
        {
            id: 'vehicleRegistration',
            title: 'Abholschein vom Händler',
            body: 'Wenn Sie den Neuwagen in Vertretung abholen, benötigen Sie zusätzlich eine Vollmacht bzw. das ausgefüllte Vollmachtsfeld auf dem Abholschein. Bitte bringen Sie in diesem Fall auch eine Kopie des Personalausweises oder Reisepasses des Fahrzeughalters mit. Bei Abholungen von Firmenfahrzeugen ist der Firmenstempel auf dem Abholschein notwendig.',
            checked: false,
        },
        { id: 'personalID', title: 'Gültiger Personalausweis oder Reisepass', body: '', checked: false },
        { id: 'driversLicense', title: 'Führerschein', body: '', checked: false },
        { id: 'vehicleLicense', title: 'Fahrzeugschein', body: '', checked: false },
        {
            id: 'licensePlates',
            title: 'Kennzeichen Ihres neuen Fahrzeugs',
            body: 'Sollten Sie keine Kennzeichenträger zur Abholung mitbringen, erhalten Sie welche von der Autostadt.',
            checked: false,
        },
        {
            id: 'documents',
            title: 'Dokumente vorab übermitteln',
            body: '(empfohlen) Schicken Sie Ihre Unterlagen bequem digital an uns. So können wir sie vorab auf Vollständigkeit prüfen und Sie sparen Zeit an Ihrem Abholtag. Der Online Check-in ist noch bis 24 Stunden vor Ihrem Abholtermin möglich. Nutzen Sie zum Übermitteln der Dokumente einfach und schnell Ihr Smartphone.',
            checked: false,
        },
    ];

    const atHomeChecklistItems = [
        { id: 'myVolkswagenID', title: 'Meine Volkswagen ID', body: '', checked: false },
        {
            id: 'volkswagenID',
            title: 'Sie haben noch keine Volkswagen ID?',
            body: 'Laden Sie die Volkswagen App kostenlos in Ihrem App Store herunter und folgen Sie Schritt für Schritt der Anleitung für die Registrierung.',
            checked: false,
        },
        {
            id: 'passwordAndSPin',
            title: 'Das dazugehörige Passwort und die S-PIN bitte nicht vergessen!',
            body: '',
            checked: false,
        }
    ];

    const electricalCarChecklistItems = [
        {
            id: 'chargeCard',
            title: 'Ladekarte schon von zuhause aus aktivieren und zur Abholung mitbringen.',
            body: 'Bei Auslieferung hat die Batterie einen Ladestand von 80 %. Anschließend können Sie mit einer Ladekarte bzw. App Ihrer Wahl (z. B. Volkswagen We Charge) an vielen Ladepunkten Strom beziehen - so auch bei uns in der Autostadt.',
            linkNotExternal: 'https://www.autostadt.de/erkunden/hallo-elektro',
            checked: false,
        },
    ];

    const [generalItems, setGeneralItems] = usePersistedChecklist('checklist-general', generalChecklistItems);
    const [atHomeItems, setAtHomeItems] = usePersistedChecklist('checklist-at-home', atHomeChecklistItems);
    const [electricalCarItems, setElectricalCarItems] = usePersistedChecklist(
        'checklist-electrical',
        electricalCarChecklistItems
    );

    const handleToggleItem = <T extends { id: number | string; checked: boolean }>(
        setter: React.Dispatch<React.SetStateAction<T[]>>,
        id: number | string,
        checked: boolean
    ) => {
        setter(prev => prev.map(item => (item.id === id ? { ...item, checked } : item)));
    };

    return (
        <SubpageBlue
            title={i18next.t('ChecklistTitle')}
            className='checklist'
            headerChildren={<p className='body large'>{i18next.t('ChecklistSubline')}</p>}
        >
            <>
                <h3>{i18next.t('ChecklistYourChecklist')}</h3>
                <div className='line' />
                <p className='body large'>{i18next.t('ChecklistYourChecklistSubline')}</p>
                <Checklist
                    items={generalItems}
                    onToggle={(id, checked) => handleToggleItem(setGeneralItems, id, checked)}
                />
                <h3>{i18next.t('ChecklistFromHome')}</h3>
                <div className='line' />
                <p>
                    <Trans
                        i18nKey={'ChecklistFromHomeSubline'}
                        components={{
                            strong: <strong />,
                            br: <br />,
                        }}
                    />
                </p>
                <Checklist
                    items={atHomeItems}
                    onToggle={(id, checked) => handleToggleItem(setAtHomeItems, id, checked)}
                />
                {electricCar && (
                    <>
                        <h3>{i18next.t('ChecklistElectricCar')}</h3>
                        <div className='line' />
                        <p className='body large'>{i18next.t('ChecklistElectricCarSubline')}</p>
                        <Checklist
                            items={electricalCarItems}
                            onToggle={(id, checked) => handleToggleItem(setElectricalCarItems, id, checked)}
                        />
                    </>
                )}

                <div className='center'>
                    <svg className='checklist-illustration'>
                        <use xlinkHref='#checklist-illustration'></use>
                    </svg>
                </div>
                <div className='white-background'>
                    <Infobox headline={i18next.t('Questions')}>
                        {
                            <div>
                                <p className='body large bold'>{i18next.t('ChecklistSubHeader')}</p>
                                <p className='body large'>{i18next.t('ChecklistParagraph')}</p>
                                <p className='body large bold'>{i18next.t('Contact')}</p>

                                <Textlink
                                    type={'svg-left'}
                                    color={'dark-blue'}
                                    text={i18next.t('PhoneNumber')}
                                    svg='#as-phone'
                                    onClick={() => {
                                        const numberWithoutSpaces = i18next.t('PhoneNumber').replace(/\s+/g, '');
                                        window.location.href = `tel:${numberWithoutSpaces}`;
                                    }}
                                />

                                <Textlink
                                    type={'svg-left'}
                                    color={'dark-blue'}
                                    text={i18next.t('ContactEmailAddress')}
                                    svg='#as-email'
                                    onClick={() => {
                                        const email = i18next.t('ContactEmailAddress');
                                        window.location.href = `mailto:${email}`;
                                    }}
                                />
                            </div>
                        }
                    </Infobox>
                </div>
            </>
        </SubpageBlue>
    );
}
